import { type Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { rem, Table } from "@mantine/core";

export const StyledTableWrapper = styled.div<{ isDynamic?: boolean }>`
  overflow-x: auto; // use auto instead of scroll as chrome strangely adds padding to the bottom of the element
  scrollbar-width: auto;
  scroll-behavior: auto;
  padding-bottom: 0px;
  display: flex;
  align-items: start;
  position: relative;

  ${({ isDynamic }) =>
    isDynamic &&
    `
    /* For dynamic sections with many rows */
    max-height: calc(100vh - 150px);
    overflow-y: auto;
  `}
`;

export const DynamicSectionWrapper = styled.div`
  position: relative;

  /* Main content with hidden scrollbar but enabled scrolling */
  .main-content {
    overflow-x: auto;
    overflow-y: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */

    /* Hide scrollbar for WebKit browsers */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  /* Sticky horizontal scrollbar container */
  .sticky-scrollbar {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    overflow-x: auto;
    overflow-y: hidden;
    z-index: 10;
    height: 9px;

    /* Hide the actual scrollbar content, we just want the scrollbar */
    .scrollbar-content {
      height: 9px;
      background: transparent;
    }
  }
`;

export const StyledTable = styled(Table)`
  border: ${({ theme }) => `1px solid ${theme.colors.main[9]}`};
`;
export const StyledSpaceCell = styled.td`
  background-color: ${({ theme }) => theme.colors.background[0]};
  border: "none";
`;

export const StyledTh = styled.th<{
  align?: string;
  width?: any;
  red?: boolean;
}>`
  color: ${({ theme }) => theme.colors.thColor[0]} !important;
  border: 1px solid
    ${({ theme, red }) =>
      red ? theme.colors.thColor[0] : theme.colors.thBorder} !important;
  background-color: ${({ theme, red }) =>
    red ? theme.colors.redText : theme.colors.thBackgroundColor};
  padding: 8px 8px;
  ${({ align }) =>
    align
      ? `text-align: ${align} !important;`
      : "text-align: center !important;"};
  user-select: none;
  width: ${props => props.width ?? "auto"};
  background-clip: padding-box; //firefox border fix
`;

export const StyledSplitTh = styled.text<{ align?: string; width?: any }>`
  color: ${({ theme }) => theme.colors.thColor[0]} !important;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
`;

interface StyledTrProps {
  theme?: Theme;
  pointer?: boolean;
  hover?: boolean;
  background?: string;
}

export const StyledTr = styled.tr`
  background-color: ${({ theme, background }: StyledTrProps) =>
    background ?? theme?.colors.tdBackgroundColor[0]};

  ${({ pointer }: StyledTrProps) => (pointer ? "cursor: pointer;" : "")}

  ${({ hover, theme }: StyledTrProps) =>
    hover &&
    `transition: background-color 0.3s; \n 
    &:hover {
      background-color: ${theme?.colors.trHoverBackgroundColor[0]};
    }
  `}
`;

interface StyledTdProps {
  theme?: Theme;
  logbook?: boolean;
  center?: boolean;
  valign?: string;
  width?: string;
  withoutImportant?: boolean;
  selectable?: string;
  grayed?: boolean;
  red?: boolean;
  noBorder?: string;
}

export const StyledTd = styled.td`
  border: ${({ noBorder, theme, withoutImportant, red }) =>
    noBorder
      ? "none !important"
      : "1px solid " +
        `${red ? theme.colors.redText : theme.colors.main[9]} ${withoutImportant ? "" : "!important"};`};
  background-color: ${({ theme, grayed }) =>
    grayed ? theme.colors.inputBg[0] : ""};
  ${({ logbook }: StyledTdProps) => logbook && "padding: 4px 6px !important;"}

  ${({ center }: StyledTdProps) => center && "text-align: center;"}

    ${({ valign }: StyledTdProps) => `vertical-align: ${valign ?? "inherit"};`}

  ${({ width }: StyledTdProps) => width && "width: " + width + ";"}

  ${({ selectable }: StyledTdProps) =>
    selectable && "pointer-events: " + selectable + ";"}
  padding:7px
`;

export const PaddingTd = styled(StyledTd)`
  padding: 10px;
  font-size: 14px;
`;

export const StyledTd2 = styled.td`
  border: 1px solid
    ${({ theme, withoutImportant }) =>
      `${theme.colors.background[0]} ${withoutImportant ? "" : ""};`};
  background-color: ${({ theme, grayed }) => theme.colors.background[0]};
  ${({ logbook }: StyledTdProps) => logbook && "padding: 4px 6px !important;"}

  ${({ center }: StyledTdProps) => center && "text-align: center;"}

    ${({ valign }: StyledTdProps) => `vertical-align: ${valign ?? "inherit"};`}
`;
export const StyledDesc = styled.div`
  max-height: 80px;
  overflow-y: auto;
  width: 100%;
  text-align: left;
`;
